/**
 * A small stale-while-revalidate cache for GET requests. Enough for a catalogue: pages show
 * cached data instantly on back/forward and refresh in the background; `invalidate` after a
 * write refreshes every mounted view whose key starts with a prefix.
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { api, ApiError } from './api'

interface Entry {
  data?: unknown
  error?: ApiError
  promise?: Promise<void>
  at: number
}

const cache = new Map<string, Entry>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function load(key: string): Promise<void> {
  const existing = cache.get(key)
  if (existing?.promise) return existing.promise
  const entry: Entry = { ...existing, at: existing?.at ?? 0 }
  entry.promise = api(key)
    .then((data) => {
      cache.set(key, { data, at: Date.now() })
    })
    .catch((error: unknown) => {
      cache.set(key, {
        data: existing?.data,
        error: error instanceof ApiError ? error : new ApiError(0, String(error)),
        at: Date.now(),
      })
    })
    .finally(emit)
  cache.set(key, entry)
  return entry.promise
}

/** Refetch every cached key that starts with one of the prefixes. */
export function invalidate(...prefixes: string[]): void {
  for (const key of cache.keys()) {
    if (prefixes.some((p) => key.startsWith(p))) {
      const e = cache.get(key)!
      cache.set(key, { ...e, at: 0 })
      void load(key)
    }
  }
}

export function useQuery<T>(key: string | null, opts: { staleMs?: number } = {}) {
  const staleMs = opts.staleMs ?? 30_000
  const subscribe = useCallback((l: () => void) => {
    listeners.add(l)
    return () => listeners.delete(l)
  }, [])
  const read = () => (key ? cache.get(key) : undefined)
  // The same snapshot on the server: prerendered pages (scripts/prerender.tsx) render with an
  // empty cache, exactly like the app's first render, so hydration matches.
  const snapshot = useSyncExternalStore(subscribe, read, read)
  const keyRef = useRef(key)
  keyRef.current = key

  useEffect(() => {
    if (!key) return
    const e = cache.get(key)
    if (!e || (!e.promise && Date.now() - e.at > staleMs)) void load(key)
  }, [key, staleMs])

  return {
    data: snapshot?.data as T | undefined,
    error: snapshot?.error,
    loading: Boolean(key) && snapshot?.data === undefined && !snapshot?.error,
    refresh: useCallback(() => (keyRef.current ? load(keyRef.current) : Promise.resolve()), []),
  }
}

/** For tests. */
export function clearCache(): void {
  cache.clear()
}
